import type { RumEvent, DefaultPrivacyLevel, RumInitConfiguration } from '@datadog/browser-rum';
import scriptLoadingHelper from '../script-loading-helper';
import optimizelyHelper from './optimizely-helper';
import { getEnv } from '@squareup/public-web-browser-helpers/environment';
import { GIT_COMMIT } from '$lib/constants/git-commit';

/**
 * Async CDN load Datadog RUM AFTER window, PWS-lite, and One Trust are loaded and
 * window.Sq.OneTrust.analyticsTrackingEnabled is allowed and not in env development (local machine)
 */
const datadogRumHelper = {
  cdnUrl: 'https://www.datadoghq-browser-agent.com/us1/v5/datadog-rum.js',
  permissions: {} as InitializationProperties,

  getConfig: (): RumInitConfiguration => ({
    applicationId: 'da021de4-34ec-4d5d-ac7f-adeb44b37761', // TODO: PWB-1148 refactor the config
    clientToken: 'pubd9af00759e65a48ba7ee3ff1dfa4260b', // TODO: PWB-1148 refactor the config
    site: 'datadoghq.com',
    service: 'pwr',
    env: getEnv(),
    version: GIT_COMMIT,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100, // adding replay for Firefox users
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    defaultPrivacyLevel: 'mask-user-input' as DefaultPrivacyLevel,
    excludedActivityUrls: [...datadogRumHelper.getDataPlatformRegexs(), datadogRumHelper.isSquareExternalHost],
    beforeSend: datadogRumHelper.beforeSend,
  }),

  initialize: async (permissions: InitializationProperties): Promise<void> => {
    datadogRumHelper.permissions = permissions;
    const env = getEnv();
    try {
      if (env !== 'development' && permissions.analyticsTrackingEnabled) {
        scriptLoadingHelper.injectClientScript('datadog-rum-script-tag', datadogRumHelper.cdnUrl, 'afterInteractive', {
          async: true,
          onError: async () => {
            // try ESM fallback first, then log
            try {
              console.warn('Datadog RUM CDN script failed to load; attempting ESM fallback');
              const { datadogRum } = await import('@datadog/browser-rum');
              datadogRum.init(datadogRumHelper.getConfig());
              // ensure replay is available; otherwise fail explicitly
              if (typeof datadogRum.startSessionReplayRecording === 'function') {
                datadogRum.startSessionReplayRecording();
              } else {
                console.error('Datadog RUM Session Replay API not available (ESM)');
              }
              // mirror post-init behavior
              datadogRumHelper.applyPostInit(datadogRum);
            } catch (e) {
              console.error('Datadog RUM ESM fallback failed to load', e);
            }
          },
          onLoad: () => {
            datadogRumHelper.load();
          },
        });
      }
    } catch (err) {
      console.error(err);
    }
  },

  load: () => {
    // This should never be called unless DD_RUM script has been loaded, but just to be safe
    if (!window.DD_RUM || !window.DD_RUM.onReady) {
      return;
    }
    window.DD_RUM.onReady(() => {
      window.DD_RUM.init(datadogRumHelper.getConfig());
      // ensure replay is available; otherwise fail explicitly
      if (typeof window.DD_RUM.startSessionReplayRecording === 'function') {
        window.DD_RUM.startSessionReplayRecording();
      } else {
        console.error('Datadog RUM Session Replay API not available (CDN)');
      }

      // mirror post-init behavior
      datadogRumHelper.applyPostInit(window.DD_RUM);
    });
  },

  // shared post-init to avoid duplication across CDN and ESM init paths
  applyPostInit: (api: {
    setGlobalContextProperty: (k: string, v: unknown) => void;
    addError: (e: unknown) => void;
  }) => {
    const optimizelyMetaTags = optimizelyHelper.getAllMetadata();
    if (optimizelyMetaTags !== undefined) {
      api.setGlobalContextProperty('optimizely', optimizelyMetaTags);
    }

    // set CDP entity info and AVT if available from cookies
    api.setGlobalContextProperty('entity', {
      entityId: datadogRumHelper.permissions.entityId,
      entityType: datadogRumHelper.permissions.entityType,
      anonymousId: datadogRumHelper.permissions.AVT,
    });

    // flush queued errors after initialization (guard existence)
    const queue = (window as any).DDErrorQueue;
    if (Array.isArray(queue) && queue.length > 0) {
      for (const err of queue) {
        try {
          api.addError(err);
        } catch {
          // swallow error: queue flush should not crash
        }
      }
      // Clear the queue once both platform collect the error queue
      (window as any).DDErrorQueue = [];
    }
  },

  logAction: (actionName: string, actionPayload: any = {}) => {
    window.DD_RUM &&
      window.DD_RUM.onReady(() => {
        window.DD_RUM.addAction(actionName, actionPayload);
      });
  },

  /**
   * Provides regexs that matches the data platform endpoints to exclude in datadog Source:
   * https://github.com/squareup/dashboard/pull/77067/files
   */
  getDataPlatformRegexs: (): RegExp[] => [
    /https:\/\/data-platform(?:-staging)?\.squarecloudservices\.com\/v1\/batch/,
    /^(https?:\/\/)(api\.square(?:up|stagingexternal)\.com|localhost:3000)\/v1\/cdp\/batch$/,
  ],

  /**
   * Function to check if a URL is a external to square and thus should be excluded from activity detection Source:
   * https://github.com/squareup/dashboard/pull/77067/files
   */
  isSquareExternalHost: (url: string): boolean => {
    try {
      const urlObject = new URL(url);
      const host = urlObject.hostname;
      return !host.includes('square') && !host.includes('localhost');
    } catch {
      return false;
    }
  },

  /**
   * Check if an user agent is for a known bot User agent reference:
   * https://square.datadoghq.com/s/3b6b4a9c0/z7c-498-3fg
   */
  isBotUserAgent: (userAgent: string): boolean => {
    const regex = /(headless|bot|crawl|spider|monkey|yeti|facebook)/i;
    return regex.test(userAgent);
  },

  /**
   * RUM beforeSend callback function to filter out events Return true if the event should be sent to Datadog Return
   * false if the event should be filtered out Reference:
   * https://docs.datadoghq.com/real_user_monitoring/guide/enrich-and-control-rum-data
   */
  beforeSend: (event: RumEvent, _context: unknown): boolean => {
    // Filter out `Object Not Found Matching Id` errors
    // These are errors without customer impact, primarily related to bot traffic
    // Reference: https://docs.datadoghq.com/real_user_monitoring/guide/enrich-and-control-rum-data/?tab=event#discard-a-frontend-error
    if (event.type === 'error' && event.error?.message?.includes('Object Not Found Matching Id')) {
      return false;
    }

    // Filter out all bot events (by user agent)
    const { userAgent } = window.navigator;
    if (datadogRumHelper.isBotUserAgent(userAgent)) {
      return false;
    }

    return true;
  },
};

export default datadogRumHelper;
